import { useMemo } from 'react';
import dayjs from 'dayjs';
import { GetInfluencerProductRequestResponse } from '@Saral-Influencer-Marketing/saral-utils/dist/types/productRequests';
import { PRODUCT_REQUEST_STATUS } from '@Saral-Influencer-Marketing/saral-utils/dist/constants/productRequests';

import styles from './alreadyRequestedProducts.module.css';
import { formatCurrency } from '../../../../utils/global';
import ButtonComponent from '../../../common/button-component/button-component';
import NewTabIcon from '../../../../assets/icons/NewTabIcon';
import useIsMobile from '../../../../hooks/useIsMobile';
import { TruncatedText } from '../../../common';

interface Props {
  product: GetInfluencerProductRequestResponse;
  defaultCurrency: string;
  viewProduct: () => void;
}

export default function AlreadyRequestedProductCard({ product, defaultCurrency, viewProduct }: Props) {
  const productsInOrder = product.order_details || [];
  const reminderBaseCount = 4;
  const isMobile = useIsMobile();
  const remainder = productsInOrder?.length || 0 % reminderBaseCount;
  const skeletonCount = 4 - remainder;

  const slicedProductsInOrder = productsInOrder?.slice(0, 4);

  const totalProducts = productsInOrder?.length || 0;
  const totalAmount = productsInOrder?.reduce(
    (acc: number, order: any) => acc + (order?.productDetails?.productPrice || 0) * (order?.quantity || 1),
    0
  );

  const { color, bgColor, text } = useMemo(() => {
    if (product.status === PRODUCT_REQUEST_STATUS.PENDING) {
      return {
        color: 'var(--text-yellow)',
        bgColor: 'var(--secondary-surface-yellow)',
        text: 'Pending',
      };
    }
    if (product.status === PRODUCT_REQUEST_STATUS.APPROVED) {
      return {
        color: 'var(--text-success)',
        bgColor: 'var(--secondary-surface-green)',
        text: 'Approved',
      };
    }
    if (product.status === PRODUCT_REQUEST_STATUS.REJECTED) {
      return {
        color: 'var(--text-critical)',
        bgColor: 'var(--critical-r-50)',
        text: 'Rejected',
      };
    }
    return {
      color: 'transparent',
      bgColor: 'transparent',
      text: '',
    };
  }, [product.status]);

  const handleButtonClick = () => {
    if (product.tracking_link) {
      const trackingLink = product.tracking_link;
      window.open(trackingLink, '_blank');
    } else {
      viewProduct();
    }
  };

  const totalAmountFormatted = formatCurrency(totalAmount, defaultCurrency);

  return (
    <div className={styles.alreadyRequestedProductCard}>
      <div className={styles.alreadyRequestedProductCardStatus} style={{ color, backgroundColor: bgColor }}>
        {text}
      </div>
      <div className={styles.alreadyRequestedProductCardThumbnail}>
        {slicedProductsInOrder?.map((order: any, index: number) => {
          return (
            <div className={styles.alreadyRequestedProductCardThumbnailImage} key={index}>
              <img src={order?.productDetails?.productImage} alt={order?.productDetails?.productName} />
            </div>
          );
        })}
        {Array.from({ length: skeletonCount }, (_, index) => (
          <div className={styles.alreadyRequestedProductCardThumbnailImage} key={index}>
            <div className={styles.alreadyRequestedProductCardThumbnailImageSkeleton} />
          </div>
        ))}
      </div>
      <div>
        <div className={`flex flex-start gap-1 items-center text-default ${isMobile ? 'text-xs' : 'text-sm'}`}>
          <div className="flex flex-start gap-1 items-center">
            {isMobile ? (
              <TruncatedText text={`${totalProducts} Product${totalProducts > 1 ? 's' : ''}`} maxLines={1} width={65} />
            ) : (
              `${totalProducts} Product${totalProducts > 1 ? 's' : ''}`
            )}
          </div>
          <div className="flex flex-start gap-1 items-center font-medium">
            <span className="text-lightest-grey">&#9679;</span> {totalAmountFormatted}
          </div>
        </div>
        <p className="text-xs text-subdued mt-1">
          {isMobile ? '' : 'Requested on '}
          {dayjs(product?.created_at).format('DD MMM, YYYY')}
        </p>
      </div>
      <ButtonComponent variant="primaryOutline" fullWidth className="h-9 cursor-pointer" onClick={handleButtonClick}>
        <span className={`font-medium flex items-center flex-center gap-1 ${isMobile ? 'text-xs' : 'text-sm'}`}>
          {product.tracking_link ? (
            <>
              <NewTabIcon /> Track Order
            </>
          ) : (
            'View Products'
          )}
        </span>
      </ButtonComponent>
    </div>
  );
}
